// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Routes, Route, Navigate } from 'react-router-dom';
import { AnalyticsProvider, RouteTracker, CookieConsentBanner, AnalyticsIdentity } from '@loa/analytics';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import LoginPage from './pages/LoginPage';
import BrokerageListPage from './pages/BrokerageListPage';
import BrokerageFormPage from './pages/BrokerageFormPage';
import AdvisorListPage from './pages/AdvisorListPage';
import AdvisorFormPage from './pages/AdvisorFormPage';
import ProviderListPage from './pages/ProviderListPage';
import ProviderFormPage from './pages/ProviderFormPage';
import PolicyTypeListPage from './pages/PolicyTypeListPage';
import PolicyTypeFormPage from './pages/PolicyTypeFormPage';
import TemplateListPage from './pages/TemplateListPage';
import TemplateFormPage from './pages/TemplateFormPage';
import TemplateResolutionPreviewPage from './pages/TemplateResolutionPreviewPage';
import TemplateUsageDashboardPage from './pages/TemplateUsageDashboardPage';
import BrokerageProvidersPage from './pages/BrokerageProvidersPage';
import BrokerageIntegrationsPage from './pages/BrokerageIntegrationsPage';
import BrokerageProviderEditPage from './pages/BrokerageProviderEditPage';
import BrokerageProviderPolicyTypesPage from './pages/BrokerageProviderPolicyTypesPage';
import BrokerageProviderPolicyTypeEditPage from './pages/BrokerageProviderPolicyTypeEditPage';
import EmailNotificationDashboard from './pages/EmailNotificationDashboard';
import EmailNotificationList from './pages/EmailNotificationList';
import EmailTemplateListPage from './pages/EmailTemplateListPage';
import EmailTemplateFormPage from './pages/EmailTemplateFormPage';
import EmailTemplatePreviewPage from './pages/EmailTemplatePreviewPage';
import AuditLogViewerPage from './pages/AuditLogViewerPage';
import SupportStaffListPage from './pages/SupportStaffListPage';
import SupportStaffDetailPage from './pages/SupportStaffDetailPage';
import BrandingMigrationPage from './pages/BrandingMigrationPage';
import BrokerageBackfillPage from './pages/BrokerageBackfillPage';
import BrandingListPage from './pages/BrandingListPage';
import BrandingCreatePage from './pages/BrandingCreatePage';
import BrandingEditPage from './pages/BrandingEditPage';
import IntelligenceConfigPage from './pages/IntelligenceConfigPage';
import ProfileFormsConfigPage from './pages/ProfileFormsConfigPage';
import TaxTablesConfigPage from './pages/TaxTablesConfigPage';
import ProjectionAssumptionsConfigPage from './pages/ProjectionAssumptionsConfigPage';
import QuoteTermsPage from './pages/QuoteTermsPage';
import NewBusinessTemplatesPage from './pages/NewBusinessTemplatesPage';
import FundsRetrievalHistoryPage from './pages/FundsRetrievalHistoryPage';

function AdminAnalyticsIdentity() {
  const { user } = useAuth();
  return <AnalyticsIdentity userId={user?.username} role="PLATFORM_ADMIN" />;
}

function App() {
  return (
    <AnalyticsProvider config={{
      app: 'admin-ui',
      tenant: 'platform',
      enabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',
      debug: import.meta.env.VITE_ANALYTICS_DEBUG === 'true',
      plausible: import.meta.env.VITE_PLAUSIBLE_DOMAIN ? {
        domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN,
        apiHost: import.meta.env.VITE_PLAUSIBLE_API_HOST || undefined,
      } : undefined,
    }}>
      <CookieConsentBanner />
      <AuthProvider>
        <AdminAnalyticsIdentity />
        <RouteTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes with AdminLayout */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* Brokerages */}
            <Route path="/brokerages" element={<BrokerageListPage />} />
            <Route path="/brokerages/new" element={<BrokerageFormPage />} />
            <Route path="/brokerages/:id/edit" element={<BrokerageFormPage />} />
            <Route path="/brokerages/:brokerageId/providers" element={<BrokerageProvidersPage />} />
            <Route path="/brokerages/:brokerageId/integrations" element={<BrokerageIntegrationsPage />} />
            <Route path="/brokerages/:brokerageId/providers/:providerId/edit" element={<BrokerageProviderEditPage />} />
            <Route path="/brokerages/:brokerageId/providers/:providerId/policy-types" element={<BrokerageProviderPolicyTypesPage />} />
            <Route path="/brokerages/:brokerageId/providers/:providerId/policy-types/:policyTypeId/edit" element={<BrokerageProviderPolicyTypeEditPage />} />

            {/* Branding */}
            <Route path="/branding" element={<BrandingListPage />} />
            <Route path="/branding/new" element={<BrandingCreatePage />} />
            <Route path="/branding/:tenantId/edit" element={<BrandingEditPage />} />

            {/* Intelligence (chatbot) configuration */}
            <Route path="/intelligence-config" element={<IntelligenceConfigPage />} />

            {/* Fact-find question-set schema (RS-owned: default + per-brokerage override) */}
            <Route path="/profile-forms-config" element={<ProfileFormsConfigPage />} />

            {/* Global Irish tax tables (drive the relationship cashflow tax engine) */}
            <Route path="/tax-tables" element={<TaxTablesConfigPage />} />
            <Route path="/quote-terms" element={<QuoteTermsPage />} />
            <Route path="/new-business-templates" element={<NewBusinessTemplatesPage />} />

            {/* Layered projection assumptions (global + per-brokerage defaults) */}
            <Route path="/projection-assumptions" element={<ProjectionAssumptionsConfigPage />} />

            {/* Advisors */}
            <Route path="/advisors" element={<AdvisorListPage />} />
            <Route path="/advisors/new" element={<AdvisorFormPage />} />
            <Route path="/advisors/:id/edit" element={<AdvisorFormPage />} />

            {/* Support Staff */}
            <Route path="/support-staff" element={<SupportStaffListPage />} />
            <Route path="/support-staff/:id" element={<SupportStaffDetailPage />} />
            <Route path="/support-staff/:id/edit" element={<SupportStaffDetailPage />} />

            {/* Providers */}
            <Route path="/providers" element={<ProviderListPage />} />
            <Route path="/providers/new" element={<ProviderFormPage />} />
            <Route path="/providers/:id/edit" element={<ProviderFormPage />} />

            {/* Policy Types */}
            <Route path="/policy-types" element={<PolicyTypeListPage />} />
            <Route path="/policy-types/new" element={<PolicyTypeFormPage />} />
            <Route path="/policy-types/:id/edit" element={<PolicyTypeFormPage />} />

            {/* Templates */}
            <Route path="/templates" element={<TemplateListPage />} />
            <Route path="/templates/new" element={<TemplateFormPage />} />
            <Route path="/templates/:id/edit" element={<TemplateFormPage />} />
            <Route path="/template-preview" element={<TemplateResolutionPreviewPage />} />
            <Route path="/template-usage" element={<TemplateUsageDashboardPage />} />

            {/* Email Notifications */}
            <Route path="/email-notifications" element={<EmailNotificationDashboard />} />
            <Route path="/email-notifications/list" element={<EmailNotificationList />} />

            {/* Email Templates */}
            <Route path="/email-templates" element={<EmailTemplateListPage />} />
            <Route path="/email-templates/new" element={<EmailTemplateFormPage />} />
            <Route path="/email-templates/:id/edit" element={<EmailTemplateFormPage />} />
            <Route path="/email-templates/:id/preview" element={<EmailTemplatePreviewPage />} />

            {/* Audit Log */}
            <Route path="/audit-log" element={<AuditLogViewerPage />} />

            {/* Fund Center */}
            <Route path="/funds/retrieval-history" element={<FundsRetrievalHistoryPage />} />

            {/* System Tools */}
            <Route path="/branding-migration" element={<BrandingMigrationPage />} />
            <Route path="/brokerage-backfill" element={<BrokerageBackfillPage />} />

            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/brokerages" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </AnalyticsProvider>
  );
}

export default App;
